define((require) => {
    "use strict";

    const _             = require("underscore");
    const ImprovedModel = require("shared/zbone/ImprovedModel");
    const ViewerEvents  = require("shared/services/ViewerEvents");
    const {Logger}        = require("shared/util/Logger");
    const Preconditions = require("shared/util/Preconditions");
    const channelBridge = require("shared/ui/viewer/ChannelBridge");

    const log = new Logger("Action");

    /**
     * An action that can be attached to a widget and activated from within the Viewer. This type of action is
     * <b>not</b> used by the Editor itself, only by the Viewer, but Action behaviour when activated can be
     * different in the Editor, for example giving an alert message that a Custom JS Action can only be
     * activated when embedded on the actual client site defining the JS to call. <p>
     *
     * An action can be <i>instantaneous</i>, i.e. completely done once has been executed, or it can be
     * <i>pending</i>, i.e. triggers something that has a duration, like an opened lightbox. In the latter case,
     * such actions may be unfinished when the Viewer unloads and it will therefor call <tt>close()</tt> on
     * each such pending action. <p>
     *
     * Once an a action has been triggered directly by a user, auto play will be disabled for the Experience
     * in question, but not for its embedded or sibling Experiences. It can still be paused/unpaused, but
     * it really has no effect then. <p>
     *
     * Any action has the following type (protected) properties, where the latter two can be overridden by
     * corresponding data (instance) attributes, excluding "_" prefix e.g. "paused" attribute will default
     * to <tt>_paused</tt> (type) property:
     *
     * <ul>
     * <li> TYPE:            String  - a mandatory static attribute storing the unique type/name of the action, e.g.
     *                                 "link" or "customJs". The type will be persisted as a <tt>type</tt> property as
     *                                 well, to allow for re-created of Actions based on json instance data.
     *
     * <li> _clientSpace:    Boolean - true if the action requires access to client space, false otherwise. Default false,
     *                                 for now "static" for the specific Action type.
     *
     * <li> _event:          String  - a non-persisted field storing the event to trigger when activated in the viewer.
     *                                 Default null. If specified (perhaps as an attribute), the event will be triggered like
     *                                 (event, target:Action, experienceId:String, ..)</tt>, where the .. are the arguments
     *                                 optionally returned by <tt>_activate(..)</tt>.
     *
     * <li> _pendingEvent:   String|String[]  - a non-persistent field storing the event that signals the completion of a
     *                                          pending action, fired by the action it self, like the closing of a lightbox that
     *                                          has been opened when the lightbox was executed. Default null. An instantaneous
     *                                          action can be a scene navigation that will not use this type of event. If
     *                                          specified, perhaps as an attribute, the Viewer will consider the action pending
     *                                          until handled, ensuring that the action will be <tt>close()</tt> if the Viewer
     *                                          is closed while the action is pending. The action implementation can trigger this
     *                                          action via <tt>_trigger(..)</tt>.
     *
     * <li> _stalled:         Boolean - true to stall all reachable Experiences on activation, including all parents and
     *                                  siblings, recursively, false otherwise (default). Any Experience stalled will
     *                                  also be paused. If a pending Action, the Experience(s) will be unstalled again
     *                                  when the Action completes, triggering a potential pause change to that of the
     *                                  value at stall time; otherwise the context must ensure this itself.
     * <li> _paused:          Boolean - true to pause the Experience on activation, including all parents recursively
     *                                  embedding it if any, false otherwise (default). If a pending Action, the
     *                                  Experience(s) will be started again when the Action completes; otherwise the
     *                                  context must ensure this itself. If <tt>autoPlay</tt> has been set to false
     *                                  for a given Experience, it unpausing will not resume auto play.
     * <li> _autoPlay:        Boolean - false to disable auto play on activation permanently for the current Experience
     *                                  only (not recursive!), true otherwise. Default true. If set to false on
     *                                  activation, unpausing will not start auto play again. Cannot set set to true
     *                                  again once set to false, e.g. if another Action has disabled auto play, a
     *                                  true value here will be ignored.
     * </ul>
     *
     * Sub-types must implement <tt>_activate(experienceViewModel:ExperienceViewModel):Boolean</tt> to perform the
     * actual action logic, but refrain from triggering events (unless multiple events are required, e.g. on lightbox
     * close as well), pausing the experience, etc. All the boiler plate is handled by this <tt>Action</tt> super-type
     * based on the value of the above properties. <p>
     *
     * To pass along additional arguments with triggered events, return them from <tt>_activate(..)</tt>. <p>
     *
     * In addition. a protected <tt>_log(message:String, type:String)</tt> function will be available for actual
     * implementations when logging is required.
     *
     * @class Action
     * @constructor
     */
    return ImprovedModel.extend("Action", { // TODO: why is an Action both data and behaviour

        embedded: true, // since it has an id and we do not want to call the server on sync!

        defaults: {

            /**
             * @type {String}
             * The fixed type of this Action, corresponding to the static <tt>TYPE</tt> property. <p>
             *
             * Always set in the constructor.
             */
            type: null
        },

        /**
         * @protected @type {Boolean}
         * True if this action requires access to the client page, false otherwise (default). <p>
         *
         * An action that *always* requires access to client space is for example a SalesforceCommerce QuickView Action
         * that calls client defined behaviour to open the Quickview.
         */
        _clientSpace: false,

        /**
         * @protected @type {String}
         * The event triggered when an Action cannot be activated, or if the activation fails because of an exception.
         */
        _errorEvent: ViewerEvents.VIEWER_ERROR,
        /**
         * @protected @type {String|String[]}
         * The standard Viewer event(s) to trigger when activated, if any (default no event). <p>
         *
         * For any event specified, the triggered Viewer event will be passed along the following arguments,
         * in order:
         * <ul>
         * <li> target:Action       - this action instance
         * <li> experienceId:String - the Experience id.
         * <li> ..:*                - additional var args, if any, returned via <tt>_activate(..)</tt>.
         * </ul>
         *
         * If an action needs to trigger additional events, it can uses its protected
         * <tt>_trigger(name:String, experienceViewModel:ExperienceViewModel)</tt> function to do so. <p>
         *
         * If the corresponding named attribute is defined on an instance of this action, stored as a String (array),
         * it will override this value. The attribute can also be set in <tt>_activate()</tt>, but <b>must</b> be
         * defined in <tt>ViewerEvents</tt>! <p>
         *
         * @see #pendingEvent
         * @see #_trigger
         * @see #_activate
         */
        _event: null,
        /**
         * @protected @type {String}
         * Specifies the single Viewer event the Experience Viewer will listen for in order to determine that this pending
         * Action has completed. Default null, meaning this Action is treated as an instantaneous action. <p>
         *
         * A pending Action is an action that starts something that eventually will complete. Like an opened lightbox
         * that will eventually be closed. <p>
         *
         * The specific Action implementation will trigger this event when relevant (not done via <tt>activate()</tt>
         * like for <tt>event</tt>), and this base type will ensure that any pending Action will be stored in the
         * <tt>ExperienceViewModel</tt> used. When the Experience Viewer is closed, any Actions for which this event
         * has not been triggered, will have their <tt>close()</tt> method forcefully invoked, e.g. to ensure that
         * an opened Lightbox will be closed. <p>
         *
         * The event will be triggered with the name specified here, and with the additional arguments as
         * documented for <tt>event</tt>. <p>
         *
         * If the corresponding named attribute is defined on an instance of this action, stored as a String (array),
         * it will override this value. The attribute can also be set in <tt>_activate()</tt>, but <b>must</b> be
         * defined in <tt>ViewerEvents</tt>! <p>
         *
         * @see #close
         * @see #event
         * @see #_trigger
         */
        _pendingEvent: null,
        /**
         * @protected @type {Boolean}
         * True to pause the Experience and all its parents recursively, if any, on activation. Siblings will not
         * be affected by this, e.g. if the parent Experience embeds other Experiences besides the current one,
         * those will not be paused by this (but see <tt>stalled</tt>). <p>
         *
         * If this action is a pending Action, it will be unpaused again once this Action completes, i.e. when
         * <tt>pendingEvent</tt> fires. If not a pending Action, the context using this Action must handle this
         * itself. <p>
         *
         * If the corresponding named attribute is defined on an instance of this action, it will override this value.
         * The attribute can also be set in <tt>_activate()</tt>. <p>
         *
         * On unpause, auto play will only commence again if <tt>autoPlay</tt> is true. <p>
         *
         * Default false.
         *
         * @see #autoPlay
         * @see #stalled
         */
        _paused: false,
        /**
         * @protected @type {Boolean}
         * True to stall all Experiences reachable from the top most Experience, i.e. the Experience embedding all
         * others recursively, false otherwise. Note that if the top most Experience is viewed on a direct url,
         * there may be other sibling Experience to it that cannot be stalled because of cross domain issues. <p>
         *
         * If this action is a pending Action, all reachable Experiences will be unstalled again once this Action
         * completes, i.e. when <tt>pendingEvent</tt> fires, trigger a restore of the paused state it had at stall
         * time (unless changed in the mean time). If not a pending Action, the context using this Action must
         * handle this itself. <p>
         *
         * If the corresponding named attribute is defined on an instance of this action, it will override this value.
         * The attribute can also be set in <tt>_activate()</tt>. <p>
         *
         * Default false.
         *
         * @see #paused
         */
        _stalled: false,
        /**
         * @protected @type {Boolean}
         * True to enable auto play for the Experience on activation, false to disable it permanently. Auto play
         * will only be run if the Experience is not paused. <p>
         *
         * Disabling auto run is not a recursive operation - it will only affect the Experience the user interacted
         * with. And once auto run is disabled, it will never be enabled again for said Experience; that is, if some
         * other Action has disabled auto run, a true value here will be ignored. <p>
         *
         * If the corresponding named attribute is defined on an instance of this action, it will override this value.
         * The attribute can also be set in <tt>_activate()</tt>. <p>
         *
         * Default false, i.e. auto play will be disabled on activation.
         *
         * @see #paused
         * @see #stalled
         */
        _autoPlay: false,

        constructor() {
            this.__super(); // will set all attributes if specified...

            // All Actions define a static Type property we must persist as an instance property, to re-create the
            // specific Action based on json data again. We know we have a super constructor already, so we use its
            // reference to get the static TYPE...
            const actionType = this.constructor.TYPE;

            let type = this.get("type");

            // Only empty on creation, not when loaded via json data...
            if (!type) {
                this.set("type", type = actionType, {silent: true}); // nobody listening yet anyways
            }
            Preconditions.checkState(actionType && (type === actionType), `Action type mismatch: '${type}' != '${actionType}'`);
        },

        /**
         * @protected @type {Function}
         * Logs the specified message for this action, qualified with its type and action state.
         *
         * @param {String}  message  The message to log.
         * @param {String}  [lvl]    The log level: "error", "warn", "info", or "verbose" (default). If error or warn,
         *                           an error will be reported via the error logger.
         */
        _log(message, lvl) {
            const msg = `<${this.get("type")}> ${message}: ${this}`;
            if (lvl === "error") {
                Preconditions.fail(msg, this); // will log error to console and report error via error logger when enabled
            } else if (lvl === "warn") {
                Preconditions.warn(msg, this); // will log warning to console and report error via error logger when enabled
            } else {
                log[lvl || "verbose"](msg, this);
            }
        },

        /**
         * Returns true if this action is currently considered activateable, false otherwise. The actual logic
         * implemented in <tt>activate(..)</tt> function will only be performed if this function returns true at
         * call time. <p>
         *
         * The activation can still fail, of course, even if this function returns true. <p>
         *
         * Actual sub-types can override <tt>_activateable(experienceViewModel:ExperienceViewModel): String</tt> to
         * add additional logic. The logic here will ensure an <tt>ExperienceViewModel</tt> is available and that
         * we are embedded via channels if <tt>clientSpace</tt> is true for this Action.
         *
         * @param {ExperienceViewModel} experienceViewModel The experience view model to use for this action;
         *                                                  can be falsey, in which case false is returned.
         * @return {Boolean} True if this action can be activated, false otherwise.
         *
         * @see    #activate
         */
        activateable(experienceViewModel, ...args) { // second boolean arg used for private use!
            let error;

            if (!experienceViewModel) {
                error = "No experience view model";
            } else if (this._clientSpace && !channelBridge()) {
                error = "Action can only be invoked when embedded";
            } else {
                // See if we have a specific error to report...
                error = this._activateable(experienceViewModel);
                if (error && typeof error !== "string") {
                    error = "Action is not activateable";
                }
            }

            const internal = args[0];
            if (error) {
                if (internal) {
                    this._log(`${error}, cannot activate`, "warn");
                    return error; // private
                }
                return false; // public
            } else if (internal) {
                return null; // private
            }

            return true; // public
        },

        /**
         * @protected
         * Returns the instance attribute value for the specified name, defaulting to the prototypical
         * behaviour of the similar but protected type attribute.
         *
         * @param  {String} name The attribute name, e.g. "paused" for "_paused" default; cannot be falsey.
         * @return {*} The desired value; can be falsey.
         */
        _value(name) {
            const value = this.get(name); // instance data
            // eslint-disable-next-line eqeqeq
            if (value != null) {
                return value;
            }

            return this[`_${name}`]; // prototype data, protected!
        },

        /**
         * @private
         * Optionally triggers the ExperienceViewModel '"change:" + name = true' event, and the reverse when
         * the <tt>pendingEvent</tt> is fired (if any).
         *
         * @param  {String}              name                The attribute and event name to trigger, e.g.
         *                                                   "paused" or "stalled"; cannot be falsey.
         * @param  {ExperienceViewModel} experienceViewModel The model to potential trigger the change on; cannot
         *                                                   be falsey.
         *
         * @return {Boolean} True the view model was tried set with a true value (which may not trigger a change),
         *                   false otherwise.
         */
        _triggerChange(name, experienceViewModel) { // not called for "autoPlay" change!
            const value = this._value(name);
            // The test for a true value here is intentional - we do not care about false here; that is, a value
            // of false should not trigger a change...
            if (value) {
                experienceViewModel.set(name, true);

                const pendingEvent = this._value("pendingEvent");
                if (pendingEvent) {
                    this.listenToOnce(ViewerEvents, pendingEvent, () => {
                        experienceViewModel.set(name, false);
                    });
                }
            }
            return value;
        },

        /**
         * Activates this action, triggering a relevant viewer event and potentially stops auto play. <p>
         *
         * This will delegate the actual work to
         * <tt>_activate(experienceViewModel:ExperienceViewModel, bridge:ChannelBridge)</tt>. <p>
         *
         * Any exceptions will be caught, reported, then silently suppressed and false will be returned.
         *
         * @param {ExperienceViewModel} experienceViewModel The experience view model to use for this action;
         *                                                  never falsey. If this Action is a pending Action,
         *                                                  this Action will add it self to the set of Actions
         *                                                  for the model in question, to be removed again ("closed")
         *                                                  once the Action completes or the Viewer closes, which
         *                                                  ever comes first.
         *
         * @return {Boolean} True if this action as indeed successfully activated, false otherwise.
         *
         * @see    #activateable
         */
        activate(experienceViewModel) { // TODO: what if executed multiple times?
            // Make sure we can activate this action...
            const error = this.activateable(experienceViewModel, true); // true: internal, we want the error message
            if (error) {
                this._trigger("errorEvent", experienceViewModel, {error, type: "activateable"});
                return false;
            }

            // Can be undefined...
            let eventArgs;

            // Do the work, getting back additional event arguments if required by this Action...
            try {
                eventArgs = this._activate(experienceViewModel);
            } catch (e) {
                this._log(`Activation failed: ${e}`, "error");
                this._trigger("errorEvent", experienceViewModel, {error: e, type: "activate"});
                return false;
            }

            // Action successfully activated, so trigger viewer event for this action if specified...
            this._trigger("event", experienceViewModel, eventArgs);

            // Trigger potential *non recursive* "autoPlay" change, respecting if already disabled as
            // a whole for this Experience?...
            if (experienceViewModel.get("autoPlay") && !this._value("autoPlay")) { // i.e. "AND" operation
                experienceViewModel.set("autoPlay", false); // can only disable, never re-enable!
            }

            // Trigger potential *recursive* "stalled" change?
            this._triggerChange("stalled", experienceViewModel);

            // Trigger potential *recursive* "pause" change?
            this._triggerChange("paused", experienceViewModel);

            const pendingEvent = this._value("pendingEvent");
            if (pendingEvent) {
                this._log("Pending action activated", "info");
                experienceViewModel.get("pendingActions").push(this);

                // Remove again on close..,
                this.listenToOnce(ViewerEvents, pendingEvent, () => {
                    this._log("Pending action complete", "info");

                    const actions = experienceViewModel.get("pendingActions");
                    let i = actions.length;

                    while (i--) {
                        if (actions[i] === this) {
                            actions.splice(i, 1);
                            break;
                        }
                    }

                    // An Action is not a View, so we have to stop listening manually in
                    // case we were listening to for example the ExperienceViewModel...
                    this.stopListening();
                });
            } else {
                this._log("Action activated", "info");
            }

            return true;
        },

        /**
         * Closes this Action if this Action is a pending action where <tt>pendingEvent</tt> has not been triggered
         * yet. <p>
         *
         * If this Action is not a pending Action type, this is a no-op.
         *
         * @see #pendingEvent
         */
        close() {
            // Default implementation does nothing...
        },

        /**
         * Triggers the specified Viewer event(s), e.g. "event" or "pendingEvent", passing along this action as the
         * first argument, the experience id as the second, followed by the specified additional event arguments,
         * if any. <p>
         *
         * The event name(s) supplied need not be only <tt>event</tt> or <tt>pendingEvent</tt>, but can be any
         * valid Viewer event triggered by this Action that is defined either as an attribute or as a prototype property.
         *
         * @param {String}              name                The attribute name of the event name to trigger, if any,
         *                                                  e.g. "event" or "pendingEvent"; a falsey resolved value means
         *                                                  no event will be triggered. Additional ones can be triggered
         *                                                  if defined in the action as an attribute or as a prototype
         *                                                  property.
         * @param {ExperienceViewModel} experienceViewModel The experience view model; never falsey.
         * @param {*|[]}                [eventArgs]         Additional event arguments to pass along as the 3'rd, 4'th,
         *                                                  etc arguments for the event triggering, if any. Will have
         *                                                  been obtained as the result value from <tt>_activate(..)</tt>.
         *
         * @see   #event
         * @see   #pendingEvent
         * @see   #_value
         * @see   ViewerEvents
         */
        _trigger(name, experienceViewModel, eventArgs) {
            // Either null, or String(s) storing the name of the Viewer event(s) to trigger...
            let events = this._value(name);
            if (events) {
                if (!_.isArray(events)) {
                    events = [events];
                }

                const experienceId = experienceViewModel.get("experience").get("id");
                let event;
                let args;
                let i;

                for (i = 0; i < events.length; i++) {
                    event = events[i];
                    args = [event, this, experienceId]; // Experience id is at least used by Channels

                    if (typeof eventArgs !== "undefined") {
                        if (_.isArray(eventArgs)) {
                            args = args.concat(eventArgs);
                        } else {
                            args.push(eventArgs);
                        }
                    }

                    // All ViewerEvents constants will be auto mapped to strings as required, also for inversion of
                    // control when others are listening on ViewerEvents. Hence, this will work with both strings
                    // and ViewerEvents constants...
                    ViewerEvents.trigger(...args);
                }
            }
        },

        /**
         * @protected
         * Returns true if this Action is considered activateable, false otherwise. <p>
         *
         * When invoked, this action will already be validated against running in <tt>clientSpace</tt> if so configured.
         *
         * @param  {ExperienceViewModel} experienceViewModel The experience view model; never falsey.
         * @return {String|*} If a string, a blank/null string means activateable, otherwise the error message to report
         *                    if not activateable, e.g. missing a product id for some product action. Otherwise
         *                    coerced into a boolean, where a truthy value means error, falsey means no error and thus
         *                    activateable.
         * @see    #_activate
         */
        _activateable() {
            return false; // no error by default
        },

        /**
         * @typedef ActionDomElement
         * @property {HTMLElement} element - DomElement
         * @property {Boolean} isWrapper   - Indicates if the DomElement has to wrap the children elements
         */

        /**
         * This function can be overriden.
         * Allow an {@see Action} to create an element that can be injected with the widget.
         *
         * eg: {@see AbstractWidgetView} and the {@see LinkAction} for exemple usage.
         * @param {HTMLElement} rootElement - The root element
         * @returns {ActionDomElement}
         */
        getElement(rootElement) { // eslint-disable-line no-unused-vars
            return {};
        },

        /**
         * @protected
         * @abstract
         * Activates this action.
         *
         * @param {ExperienceViewModel} experienceViewModel The experience view model; never falsey.
         * @see    #_activateable
         */
        _activate: null

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/action/Action.js